import { Helmet } from 'react-helmet-async';
import { useLocation, matchPath } from 'react-router-dom';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { routes } from '@/routes';
import { getPublicBaseUrl, getCanonicalUrl } from '@/lib/publicBaseUrl';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  schema?: Record<string, any>;
  publiclyIndexable?: boolean;
}

// Single source of truth for base domain (reads PUBLIC_BASE_URL, default: https://www.fivelensenneagram.com)
// BASE_DOMAIN = 'https://fivelensenneagram.com'
const BASE_DOMAIN = getPublicBaseUrl();
const SUPPORTED_LOCALES = ['en_ZA', 'en_GB', 'en_US'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const DEFAULT_SEO_CONFIG: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'FIVE LENS Enneagram | People Development & Team Effectiveness',
    description: 'Resolve team friction at its root. The FIVE LENS framework integrates the Enneagram with four psychological lenses for data-driven coaching and leadership development.'
  },
  '/about': {
    title: 'About FIVE LENS Enneagram | Our Story & Team',
    description: 'Meet the team behind FIVE LENS. Founded in South Africa, operating globally — decades of experience in behavioural science, coaching, and organisational development.'
  },
  '/contact': {
    title: 'Contact FIVE LENS Enneagram | Get in Touch',
    description: 'Reach our teams in Cape Town and Greater Philadelphia. Book a discovery call, enquire about certifications, or discuss enterprise solutions.'
  },
  '/practitioner-certification': {
    title: 'Enneagram Practitioner Certification | ICF 23.75 CCE | FIVE LENS',
    description: 'Become a certified FIVE LENS Enneagram practitioner. ICF-accredited (23.75 CCE), 8 weeks blended online learning. Debrief behavioural profiles with confidence.'
  },
  '/certification/team-facilitator': {
    title: 'Team Facilitator Certification | Enneagram Team Coaching | FIVE LENS',
    description: 'Facilitate team-building sessions using the FIVE LENS framework. Level 2 certification with customisable Team Skills Modules. 4 weeks online.'
  },
  '/cohorts': {
    title: 'Upcoming Certification Cohorts | FIVE LENS Enneagram',
    description: 'View upcoming Practitioner and Team Facilitator certification dates. Enrol online — live Zoom sessions with expert faculty. Limited spots per cohort.'
  },
  '/team-effectiveness': {
    title: 'Team Effectiveness Programme | Enneagram Team Assessment | FIVE LENS',
    description: 'Diagnose and resolve systemic team friction with FIVE LENS. Measurable improvements in cross-functional collaboration, trust, and decision-making.'
  },
  '/team-maturity-assessment': {
    title: 'Team Maturity Assessment | Free Organisational Diagnostic | FIVE LENS',
    description: "Assess your team's behavioural maturity across five psychological dimensions. Data-driven insights for targeted leadership and team development."
  },
  '/for-individuals': {
    title: 'Enneagram Coaching for Individuals | Personal Development | FIVE LENS',
    description: 'Discover your core behavioural drives, blind spots, and growth path. Book a one-on-one FIVE LENS Enneagram debrief with an accredited coach.'
  },
  '/personal-mastery-and-resilience': {
    title: 'Personal Mastery & Resilience | Leadership Development | FIVE LENS',
    description: 'Build emotional resilience and leadership maturity. The FIVE LENS approach measures vertical development, self-efficacy, and adaptive capacity under pressure.'
  },
  '/diagnostic': {
    title: 'Free Team Friction Diagnostic | 3-Minute Assessment | FIVE LENS',
    description: 'Take the free 3-minute diagnostic to identify the core psychological drivers causing silos, burnout, and decision paralysis in your organisation.'
  },
  '/mini-assessment': {
    title: 'Free 4-Minute Enneagram Mini Assessment | FIVE LENS',
    description: 'Answer 24 quick questions to uncover your primary Enneagram pattern, where your energy goes first, and a snapshot of your resilience under pressure. Free, no credit card required.'
  },
  '/pricing': {
    title: 'Certification Pricing | Practitioner & Team Facilitator | FIVE LENS',
    description: 'Transparent pricing for FIVE LENS certifications. Practitioner Certification, Team Facilitator Certification, and combined bundle options. ICF accredited.'
  },
  '/organizations': {
    title: 'Enneagram for Organisations | Team Alignment & Culture Change | FIVE LENS',
    description: 'Deploy data-driven behavioural interventions that align executive teams, break down silos, and turn operational drag into high-trust performance.'
  },
  '/framework': {
    title: 'The Five Lens Framework | Beyond Single-Lens Enneagram',
    description: 'Five integrated lenses — Enneagram, Personal Mastery, Emotional Resilience, Social Drives, Energy Centers — for unprecedented depth in coaching and team development.'
  },
  '/evidence-and-research': {
    title: 'Evidence & Scientific Validation | FIVE LENS Enneagram Research',
    description: 'Peer-reviewed research, PhD validation, and psychometric data behind the Five Lens Questionnaire. A scientifically grounded approach to the Enneagram.'
  },
  '/icf-cce-points': {
    title: 'ICF CCE Points | Enneagram Certification for Coaches | FIVE LENS',
    description: 'Earn 23.75 ICF Continuing Coach Education points with FIVE LENS Practitioner Certification. Recognised by the International Coaching Federation.'
  },
  '/find-a-practitioner': {
    title: 'Find a Certified FIVE LENS Enneagram Coach | Practitioner Directory',
    description: 'Search our global directory of accredited FIVE LENS Enneagram practitioners. Find a certified coach for personal debriefs or team facilitation.'
  },
  '/practitioners': {
    title: 'Find a Certified FIVE LENS Enneagram Coach | Practitioner Directory',
    description: 'Search our global directory of accredited FIVE LENS Enneagram practitioners. Find a certified coach for personal debriefs or team facilitation.'
  },
  '/blog': {
    title: 'Blog | Enneagram Insights for Coaches & Leaders | FIVE LENS',
    description: 'Articles on team effectiveness, leadership development, Enneagram coaching, and the science of behavioural assessment. Written for coaches, HR leaders, and practitioners.'
  },
  '/resource-library': {
    title: 'Resource Library | Guides & Tools | FIVE LENS Enneagram',
    description: 'Downloadable guides, research papers, and practical tools for coaches and HR professionals using the Enneagram in organisations.'
  },
  '/lens/enneagram': {
    title: 'The Enneagram Lens | Core Motivations & Defence Mechanisms | FIVE LENS',
    description: 'Understand core motivations, fears, and internal defence mechanisms that drive behaviour. The foundational lens in the FIVE LENS framework.'
  },
  '/lens/personal-mastery': {
    title: 'Personal Mastery & Maturity Lens | Vertical Development | FIVE LENS',
    description: 'Measure resilience, self-efficacy, and leadership maturity. The Personal Mastery lens assesses vertical development and capacity for organisational complexity.'
  },
  '/lens/emotional-resilience': {
    title: 'Emotional Resilience Lens | Stress Recovery & Adaptability | FIVE LENS',
    description: 'Assess capacity to adapt, recover, and maintain clarity under strain. The Emotional Resilience lens maps stress recovery for coaching and team development.'
  },
  '/lens/social-drives': {
    title: 'Social Drives Lens | Status, Connection & Belonging | FIVE LENS',
    description: 'Analyse how individuals navigate status, connection, and belonging within teams. Uncover fundamental motivations and unmet needs driving team dynamics.'
  },
  '/lens/energy-centres': {
    title: 'Energy Centers Lens | Instinctual, Emotional & Intellectual | FIVE LENS',
    description: 'Explore how individuals process information and make decisions using three core centres. The Energy Centers lens completes the FIVE LENS framework.'
  },
  '/reports/standard': {
    title: 'Standard Personal Feedback Report | FIVE LENS Enneagram',
    description: 'In-depth individual behavioural profile covering all five lenses. Used by certified practitioners for one-on-one coaching debriefs.'
  },
  '/reports/premium': {
    title: 'Premium Personal Feedback Report | FIVE LENS Enneagram',
    description: 'Comprehensive personal development report mapping Enneagram, vertical development, resilience, social drives, and decision-making styles.'
  }
};

const GLOBAL_DEFAULT = {
  title: 'FIVE LENS Enneagram | Behavioral Insights',
  description: 'A deeply integrated approach to people development and behavioral insight.'
};

/**
 * Parses a pathname to detect whether the user is viewing a locale variant (en_ZA, en_GB, en_US)
 * or the default unprefixed URL, and extracts the clean route path.
 */
function detectLocaleAndPath(locationPathname: string): {
  currentLocale: SupportedLocale | null;
  cleanPath: string;
} {
  let fullPath = locationPathname || '/';
  if (typeof window !== 'undefined' && window.location?.pathname) {
    fullPath = window.location.pathname;
  }
  fullPath = fullPath.split('?')[0].split('#')[0];

  const match = fullPath.match(/^\/(en_ZA|en_GB|en_US)($|\/.*)/i);
  if (match) {
    const rawLocale = match[1].toLowerCase();
    let currentLocale: SupportedLocale = 'en_ZA';
    if (rawLocale === 'en_gb') currentLocale = 'en_GB';
    if (rawLocale === 'en_us') currentLocale = 'en_US';

    let rest = match[2] || '/';
    if (!rest.startsWith('/')) rest = '/' + rest;
    rest = rest.replace(/\/+/g, '/');
    return {
      currentLocale,
      cleanPath: rest,
    };
  }

  const ssrMatch = locationPathname.match(/^\/(en_ZA|en_GB|en_US)($|\/.*)/i);
  if (ssrMatch) {
    const rawLocale = ssrMatch[1].toLowerCase();
    let currentLocale: SupportedLocale = 'en_ZA';
    if (rawLocale === 'en_gb') currentLocale = 'en_GB';
    if (rawLocale === 'en_us') currentLocale = 'en_US';

    let rest = ssrMatch[2] || '/';
    if (!rest.startsWith('/')) rest = '/' + rest;
    rest = rest.replace(/\/+/g, '/');
    return {
      currentLocale,
      cleanPath: rest,
    };
  }

  let clean = locationPathname.startsWith('/') ? locationPathname : '/' + locationPathname;
  clean = clean.replace(/\/+/g, '/');
  return {
    currentLocale: null,
    cleanPath: clean,
  };
}

/**
 * Builds full absolute URLs for hreflang alternates and canonicals.
 * Following specification:
 *   en-ZA     -> https://fivelensenneagram.com/en_ZA/[path]
 *   en-GB     -> https://fivelensenneagram.com/en_GB/[path]
 *   en-US     -> https://fivelensenneagram.com/en_US/[path]
 *   x-default -> https://fivelensenneagram.com/[path]
 */
function buildLocaleUrl(locale: SupportedLocale | 'x-default', cleanPath: string): string {
  const base = getPublicBaseUrl();
  const normalizedPath = cleanPath.replace(/^\/+/, '').replace(/\/+$/, '');
  const isRoot = !normalizedPath;

  if (locale === 'x-default') {
    return isRoot
      ? `${base}/`
      : `${base}/${normalizedPath}`;
  }

  return isRoot
    ? `${base}/${locale}/`
    : `${base}/${locale}/${normalizedPath}`;
}

export const SEOHead = ({ 
  title, 
  description, 
  canonicalUrl, 
  ogImage,
  schema,
  publiclyIndexable
}: SEOHeadProps) => {
  const { pathname } = useLocation();
  const { seoOverrides } = useSiteSettings();

  const { currentLocale, cleanPath } = detectLocaleAndPath(pathname);

  // Match current path to route configuration
  const matchedRoute = routes.find(r => {
    if (r.path === '*') return false;
    return Boolean(matchPath({ path: r.path, end: true }, cleanPath));
  });

  const isBlogPost = matchedRoute?.path === '/blog/:id' || (cleanPath !== '/blog' && cleanPath.startsWith('/blog/'));
  const isIndexable = !isBlogPost && (publiclyIndexable !== undefined 
    ? publiclyIndexable 
    : (matchedRoute ? matchedRoute.publiclyIndexable : false));

  // 1. Check if admin has explicitly overridden this path in Site Settings
  const override = seoOverrides?.find(o => o.path === cleanPath || o.path === cleanPath + '/' || o.path === pathname || o.path === pathname + '/');

  // 2. Lookup standard route map
  const routeDefault = matchedRoute?.seo || DEFAULT_SEO_CONFIG[cleanPath] || GLOBAL_DEFAULT;

  const localeSeo = currentLocale && (matchedRoute?.seo as any)?.byLocale
    ? (matchedRoute!.seo as any).byLocale[currentLocale]
    : undefined;

  // 3. Cascade logic: Admin Override > Dynamic Component Props > Standard Route Default
  const finalTitle = override?.title || localeSeo?.title || title || routeDefault.title;
  const finalDescription = override?.description || localeSeo?.description || description || routeDefault.description;

  // 4. Calculate self-referential canonical URL
  let targetCleanPath = cleanPath;
  const rawCanonical = override?.canonicalUrl || canonicalUrl;
  if (rawCanonical) {
    try {
      if (rawCanonical.startsWith('http://') || rawCanonical.startsWith('https://')) {
        const parsed = new URL(rawCanonical);
        targetCleanPath = parsed.pathname;
      } else {
        targetCleanPath = rawCanonical;
      }
    } catch {
      targetCleanPath = rawCanonical;
    }
    targetCleanPath = targetCleanPath.replace(/^\/(en_US|en_GB|en_ZA)/i, '') || '/';
    if (!targetCleanPath.startsWith('/')) targetCleanPath = '/' + targetCleanPath;
    targetCleanPath = targetCleanPath.replace(/\/+/g, '/');
  }

  const finalCanonicalUrl = currentLocale
    ? buildLocaleUrl(currentLocale, targetCleanPath)
    : getCanonicalUrl(targetCleanPath);

  const finalOgImage = override?.ogImage || ogImage || `${getPublicBaseUrl()}/og-image.jpg`;
  
  let finalSchema = schema;
  if (override?.schema) {
    try {
      finalSchema = JSON.parse(override.schema);
    } catch (e) {
      console.warn('Invalid JSON in SEO Schema Override for path', cleanPath);
    }
  }

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href={finalCanonicalUrl} />
      {!isIndexable && <meta name="robots" content="noindex, nofollow" />}
      {finalSchema && (
        <script type="application/ld+json">
          {JSON.stringify(finalSchema)}
        </script>
      )}
    </Helmet>
  );
};
